import * as THREE from 'three';
import { WEAPONS, characterFromIndex, clamp, lerp, type WeaponId } from '@game/shared';

interface WeaponVisual {
  group: THREE.Group;
  /** Resting position in view space, hip-fire and ADS. */
  hip: THREE.Vector3;
  ads: THREE.Vector3;
}

/** Sleeve and hand materials, shared across weapons and re-tinted per character. */
interface ArmMaterials {
  sleeve: THREE.MeshLambertMaterial;
  skin: THREE.MeshLambertMaterial;
}

/**
 * A sleeve box running from `from` (off the bottom edge of the screen, where
 * the shoulder would be) to `to` (a hand on the weapon).
 */
const forearm = (
  material: THREE.Material,
  from: THREE.Vector3,
  to: THREE.Vector3,
  thickness: number,
): THREE.Mesh => {
  const length = from.distanceTo(to);
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(thickness, thickness, length),
    material,
  );
  mesh.position.copy(from).lerp(to, 0.5);
  mesh.lookAt(to);
  return mesh;
};

const buildWeapon = (id: WeaponId, arms: ArmMaterials): WeaponVisual => {
  const group = new THREE.Group();
  const metal = new THREE.MeshLambertMaterial({ color: 0x454d5e });
  const grip = new THREE.MeshLambertMaterial({ color: 0x2b303b });
  const accent = new THREE.MeshLambertMaterial({ color: 0x4cc2ff });

  const dimensions: Record<WeaponId, [number, number, number]> = {
    smg: [0.09, 0.11, 0.42],
    rifle: [0.09, 0.12, 0.62],
    shotgun: [0.11, 0.14, 0.66],
    sniper: [0.08, 0.11, 0.8],
  };
  const [width, height, length] = dimensions[id];

  const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, length), metal);
  body.position.z = -length / 2;
  group.add(body);

  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.018, 0.018, length * 0.55, 8),
    metal,
  );
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, height * 0.1, -length * 0.9);
  group.add(barrel);

  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.16, 0.07), grip);
  handle.position.set(0, -height * 0.85, -length * 0.25);
  handle.rotation.x = -0.2;
  group.add(handle);

  // Stock toward the shoulder, so the receiver no longer ends in a bare slab.
  const stock = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.8, height * 0.85, 0.18),
    grip,
  );
  stock.position.set(0, -height * 0.2, 0.05);
  group.add(stock);

  if (id === 'smg' || id === 'rifle') {
    const magazine = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.17, 0.09), grip);
    magazine.position.set(0, -height * 0.5 - 0.08, -length * 0.42);
    magazine.rotation.x = 0.12;
    group.add(magazine);
  }

  // The hands that actually hold it: trigger hand on the grip, support hand
  // cupped under the fore-end, sleeves running off toward the bottom corners
  // of the screen. Parented to the weapon so they inherit sway, bob and kick —
  // the gun is carried, not floating.
  const rightHand = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.09, 0.11), arms.skin);
  rightHand.position.set(0, -height * 0.85, -length * 0.25);
  group.add(rightHand);
  group.add(
    forearm(
      arms.sleeve,
      new THREE.Vector3(0.16, -0.3, 0.14),
      new THREE.Vector3(0.01, -height * 0.85 - 0.02, -length * 0.25 + 0.04),
      0.095,
    ),
  );

  const supportZ = -length * 0.6;
  const leftHand = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.09, 0.11), arms.skin);
  leftHand.position.set(0, -height * 0.5 - 0.04, supportZ);
  group.add(leftHand);
  group.add(
    forearm(
      arms.sleeve,
      new THREE.Vector3(-0.2, -0.34, 0.06),
      new THREE.Vector3(-0.01, -height * 0.5 - 0.06, supportZ + 0.05),
      0.095,
    ),
  );

  /**
   * Height of the sight line above the weapon's origin. ADS lands this on the
   * camera's centre line, which is why it also has to clear the receiver: if the
   * sight line ran through the body, aiming would just put a solid block over
   * the crosshair.
   */
  const sightY = height * 0.5 + 0.035;

  if (id === 'sniper') {
    // Open-ended cylinder: an actual tube you look through, not a capped rod.
    // DoubleSide is required or the inside of the tube renders as nothing.
    const scope = new THREE.Mesh(
      new THREE.CylinderGeometry(0.032, 0.032, 0.24, 16, 1, true),
      new THREE.MeshLambertMaterial({ color: 0x22262f, side: THREE.DoubleSide }),
    );
    scope.rotation.x = Math.PI / 2;
    scope.position.set(0, sightY, -length * 0.32);
    group.add(scope);
  } else {
    // Rear aperture: a ring, so the centre of the screen stays clear.
    const rear = new THREE.Mesh(new THREE.TorusGeometry(0.016, 0.004, 6, 16), metal);
    rear.position.set(0, sightY, -length * 0.14);
    group.add(rear);

    // Front blade, tip exactly on the sight line — the thing you actually align.
    const front = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.022, 0.005), accent);
    front.position.set(0, sightY - 0.011, -length * 0.95);
    group.add(front);
  }

  return {
    group,
    // Held a touch lower and further out than the old floating block, so the
    // weapon frames the corner of the screen instead of dominating it.
    hip: new THREE.Vector3(0.16, -0.18, -0.38),
    // Drops the weapon by exactly the sight height, so aiming looks *through*
    // the sight rather than at the receiver. The scope needs more eye relief.
    ads: new THREE.Vector3(0, -sightY, id === 'sniper' ? -0.16 : -0.26),
  };
};

/**
 * First-person weapon model. Rendered by a separate camera with its own depth
 * pass (see SceneRenderer), so it never clips into walls. Sway, bob and the
 * recoil kick are all cosmetic — none of it touches the simulation.
 */
export class ViewModel {
  private readonly weapons = new Map<WeaponId, WeaponVisual>();
  private current: WeaponVisual;
  private currentId: WeaponId = 'smg';

  private readonly position = new THREE.Vector3();
  private readonly sway = new THREE.Vector2();
  private bobPhase = 0;
  private kick = 0;
  private adsAmount = 0;
  private shown = true;

  // Shared by every weapon's hands and sleeves; re-tinted by `setCharacter`.
  // Defaults are Ace, the character everyone starts as.
  private readonly arms: ArmMaterials = {
    sleeve: new THREE.MeshLambertMaterial({ color: 0x2c5a94 }),
    skin: new THREE.MeshLambertMaterial({ color: 0xf0c48c }),
  };

  constructor(private readonly scene: THREE.Scene) {
    for (const id of Object.keys(WEAPONS) as WeaponId[]) {
      const visual = buildWeapon(id, this.arms);
      visual.group.visible = false;
      scene.add(visual.group);
      this.weapons.set(id, visual);
    }
    this.current = this.weapons.get('smg')!;
    this.current.group.visible = true;
    this.position.copy(this.current.hip);
  }

  /** Tints the first-person sleeves and hands to the chosen character. */
  setCharacter(index: number): void {
    const def = characterFromIndex(index);
    this.arms.sleeve.color.setHex(def.sleeves);
    this.arms.skin.color.setHex(def.skin);
  }

  setWeapon(id: WeaponId): void {
    if (id === this.currentId) return;
    this.current.group.visible = false;
    this.currentId = id;
    this.current = this.weapons.get(id) ?? this.current;
    this.current.group.visible = this.shown;
  }

  /** Hidden in the lobby — there is nothing to hold when weapons are dead. */
  setVisible(shown: boolean): void {
    if (shown === this.shown) return;
    this.shown = shown;
    this.current.group.visible = shown;
  }

  /** Called when the local player fires; drives the visual punch. */
  onFire(): void {
    this.kick = Math.min(1, this.kick + 0.6);
  }

  /**
   * @param speed horizontal speed, for walk bob
   * @param yawDelta / pitchDelta view movement since last frame, for sway
   */
  update(
    dt: number,
    speed: number,
    yawDelta: number,
    pitchDelta: number,
    ads: boolean,
    onGround: boolean,
  ): void {
    this.adsAmount = clamp(this.adsAmount + (ads ? dt * 8 : -dt * 8), 0, 1);
    this.kick = Math.max(0, this.kick - dt * 6);

    // Sway lags the crosshair, giving weight to fast flicks.
    const swayScale = lerp(1, 0.25, this.adsAmount);
    this.sway.x =
      lerp(this.sway.x, clamp(-yawDelta * 6, -0.06, 0.06), dt * 12) * swayScale;
    this.sway.y =
      lerp(this.sway.y, clamp(-pitchDelta * 6, -0.06, 0.06), dt * 12) * swayScale;

    const bobStrength = onGround
      ? Math.min(1, speed / 8) * lerp(1, 0.2, this.adsAmount)
      : 0;
    this.bobPhase += dt * speed * 1.1;
    const bobX = Math.cos(this.bobPhase) * 0.012 * bobStrength;
    const bobY = Math.abs(Math.sin(this.bobPhase)) * 0.014 * bobStrength;

    const rest = this.current.hip.clone().lerp(this.current.ads, this.adsAmount);
    this.position.lerp(rest, Math.min(1, dt * 14));

    const group = this.current.group;
    group.position.set(
      this.position.x + this.sway.x + bobX,
      this.position.y + this.sway.y - bobY,
      this.position.z + this.kick * 0.06,
    );
    group.rotation.set(this.kick * 0.14 + this.sway.y * 0.6, this.sway.x * 0.8, 0);
  }

  /** World-space muzzle position, for the flash light. */
  muzzleWorldPosition(out: THREE.Vector3): THREE.Vector3 {
    return out.set(0, this.position.y + 0.02, this.position.z - 0.5);
  }

  dispose(): void {
    for (const visual of this.weapons.values()) {
      this.scene.remove(visual.group);
      visual.group.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          (object.material as THREE.Material).dispose();
        }
      });
    }
    this.weapons.clear();
  }
}
